import { Box, ListItem } from '@mui/material';
import { CardItem, ItemType } from './CardTab';
import { useMemo } from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ReactComponent as UpdateArrow } from '@src/assets/icons/update_arrow.svg';

export interface LineItem {
  id: string;
  createdAt: Date;
  inventory: {
    id: string;
    name: string;
  };
  qtyOnHand: {
    current: number;
    old?: number;
    isDifferent: boolean;
  };
  qtyOpen: {
    current: number;
    old?: number;
    isDifferent: boolean;
  };
  isLoan: {
    current: boolean;
    old?: boolean;
    isDifferent: boolean;
  };
  type: ItemType;
  hasUpdate: boolean;
}

interface Props {
  isShowChanges: boolean;
  cardItem: CardItem;
}

const lineBackColour = (type: ItemType, theme: Theme) => {
  switch (type) {
    case ItemType.ItemAdded:
      return 'rgba(0, 184, 144, 0.08)'; //theme.palette.success.dark;
    case ItemType.ItemRemoved:
      return 'rgba(241, 67, 54, 0.08)'; //theme.palette.error.dark';
    case ItemType.ItemUpdated:
      return 'rgba(255, 171, 0, 0.08)'; //theme.palette.warning.dark;
    default:
      return '';
  }
};

function CardItemLine({ isShowChanges, cardItem }: Props) {
  const theme = useTheme();

  const item = useMemo(() => {
    const mainItem = cardItem;

    if (!mainItem) {
      return;
    }

    let isLoanDifferent = true;

    if (
      mainItem.previous &&
      mainItem.previous.isLoan &&
      mainItem.current.isLoan
    ) {
      isLoanDifferent = false;
    } else if (
      (!mainItem.previous && !mainItem.current.isLoan) ||
      (!mainItem.previous?.isLoan && !mainItem.current.isLoan)
    ) {
      isLoanDifferent = false;
    }

    let lineItem: LineItem = {
      id: mainItem.id,
      createdAt: mainItem.createdAt,
      inventory: mainItem.inventory,
      qtyOnHand: {
        current: mainItem.current.qtyOnHand,
        old: mainItem.previous?.qtyOnHand,
        isDifferent:
          mainItem.previous?.qtyOnHand !== mainItem.current.qtyOnHand,
      },
      qtyOpen: {
        current: mainItem.current.qtyOpen,
        old: mainItem.previous?.qtyOpen,
        isDifferent: mainItem.previous?.qtyOpen !== mainItem.current.qtyOpen,
      },
      isLoan: {
        current: mainItem.current.isLoan,
        old: mainItem.previous?.isLoan,
        isDifferent: isLoanDifferent,
      },
      type: mainItem.type,
      hasUpdate: mainItem.type === ItemType.ItemUpdated,
    };

    return lineItem;
  }, [cardItem]);

  return (
    <>
      {item && (
        <ListItem
          key={item.id}
          sx={{
            backgroundColor: isShowChanges
              ? lineBackColour(item.type, theme)
              : '',
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '50%',
            }}>
            {item && item.type === ItemType.ItemRemoved && isShowChanges && (
              <RemoveCircleIcon
                sx={(theme) => ({
                  color: theme.palette.error.dark,
                  margin: theme.spacing(1),
                })}
              />
            )}
            {item && item.type === ItemType.ItemAdded && isShowChanges && (
              <AddCircleIcon
                sx={(theme) => ({
                  color: theme.palette.success.dark,
                  margin: theme.spacing(1),
                })}
              />
            )}
            <Box sx={{ display: 'flex' }}>
              {item.inventory.name}
              {item.isLoan.current && (
                <Box
                  sx={(theme) => ({
                    paddingLeft: theme.spacing(1),
                  })}>
                  (Loan)
                </Box>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              width: '50%',
              display: 'flex',
              alignItem: 'right',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}>
            {(!item.hasUpdate || !isShowChanges) && (
              <>
                {item.qtyOpen.current > 0 && (
                  <Box>Open: {item.qtyOpen.current}</Box>
                )}
                {item.qtyOnHand.current > 0 && (
                  <Box>On Hand: {item.qtyOnHand.current}</Box>
                )}
              </>
            )}
            {item.hasUpdate && isShowChanges && (
              <>
                {(item.qtyOpen.isDifferent || item.qtyOpen.current > 0) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '250px',
                    }}>
                    {item.qtyOpen.isDifferent && (
                      <>
                        <Box
                          sx={{
                            textDecoration: 'line-through',
                            width: '100px',
                          }}>
                          Open: {item.qtyOpen.old}
                        </Box>
                        <Box sx={{ width: '50px' }}>
                          <UpdateArrow />
                        </Box>
                      </>
                    )}
                    <Box sx={{ width: '100px' }}>
                      Open: {item.qtyOpen.current}
                    </Box>
                  </Box>
                )}
                {(item.qtyOnHand.isDifferent || item.qtyOnHand.current > 0) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '250px',
                    }}>
                    {item.qtyOnHand.isDifferent && (
                      <>
                        <Box
                          sx={{
                            textDecoration: 'line-through',
                            width: '100px',
                          }}>
                          On Hand: {item.qtyOnHand.old}
                        </Box>
                        <Box sx={{ width: '50px' }}>
                          <UpdateArrow />
                        </Box>
                      </>
                    )}
                    <Box sx={{ width: '100px' }}>
                      On Hand: {item.qtyOnHand.current}
                    </Box>
                  </Box>
                )}
                {item.isLoan.isDifferent && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '250px',
                    }}>
                    <Box
                      sx={{
                        textDecoration: item.isLoan.old ? 'line-through' : '',
                        width: '100px',
                      }}>
                      {item.isLoan.old && <span>Loan</span>}
                    </Box>
                    <Box sx={{ width: '50px' }}>
                      <UpdateArrow />
                    </Box>
                    <Box
                      sx={{
                        textDecoration: item.isLoan.current
                          ? ''
                          : 'line-through',
                        width: '100px',
                      }}>
                      {item.isLoan.current && <span>Loan</span>}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        </ListItem>
      )}
    </>
  );
}

export default CardItemLine;
